import ArrowForward from '@mui/icons-material/ArrowForward';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Skeleton,
  Stack,
  styled,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { first, isEmpty } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { Globe, Mail, MapPin, Phone } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import ButtonBookmark from 'src/components/common/ButtonBookmark';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import IconFeather from 'src/components/common/Icon';
import LogoCompany from 'src/components/common/LogoCompany';
import { CreditCoinIcon } from 'src/components/Icons';
import CheckedCredit from 'src/components/Icons/CheckedCredit';
import {
  CompanyProfileTagsMapping,
  ICompanyProfileTagProps,
} from 'src/features/CompanyDetail/Common/CompanyProfileTags';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useQueryQuickViewCompanyProfile } from 'src/services/queries/useQueryCompany';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { FORMAT_DATE_TIME, UnknownText } from 'src/utils/constants/config';
import { TypeCompanyProfileType } from 'src/utils/constants/enum';
import { FeatureAction } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { checkCountryHaveState, convertToStartCase, getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  height: 48,
  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  textAlign: 'center',
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

interface IProps {
  selected?: ICompanyProfile;
  handleOpenBookmarkNoteForm: (companyId: string, companyHasBookmark: boolean) => void;
  isBookmark: boolean;
}

const CompanyProfileDetail = ({ selected, handleOpenBookmarkNoteForm, isBookmark }: IProps) => {
  const navigate = useNavigate();
  // react-query hooks
  const {
    data: { data },
    isFetching,
  } = useQueryQuickViewCompanyProfile(selected?.id || '');
  // Custom hooks
  const { getFeaturePoints } = useFeatureAccess();

  const pointsView = getFeaturePoints(URL_ENDPOINT.company.companies + 'detail/ai/{id}', FeatureAction.VIEW);
  // const pointsView = useGetPointsFeature(LIST_FEATURES.COMPANY_PROFILE_COMPANY_OVERVIEW, LIST_ACTIONS.VIEW);
  const {
    data: { data: runTimeConfig },
  } = useQueryRuntimeConfig();

  const isCountryHaveState = checkCountryHaveState(selected?.country || '');
  const country = getCountryByCountryCode(selected?.country);
  const deadJurisdictions = runTimeConfig?.DEAD_JURISDICTIONS;
  const completeJurisdictions = runTimeConfig?.COMPLETE_JURISDICTIONS;
  const jurisdictionLabel = isCountryHaveState
    ? `${country.label} - ${selected?.jurisdiction}`
    : selected?.jurisdiction || '';
  const isCompleteData = completeJurisdictions?.[jurisdictionLabel];
  const isDeadData = deadJurisdictions?.[jurisdictionLabel];
  const isPendingData = !completeJurisdictions?.[jurisdictionLabel] && !deadJurisdictions?.[jurisdictionLabel];

  const companyProfileTags: ((props: ICompanyProfileTagProps) => JSX.Element)[] = useMemo(() => {
    return (
      data.official_tags?.map((tag: TypeCompanyProfileType) => {
        return CompanyProfileTagsMapping[tag];
      }) || []
    );
  }, [data.official_tags]);

  const theme = useTheme();

  const navigateToDetail = () => {
    if (selected?.id) {
      navigate(
        AuthRouteKeys.CompanyDetail.replace(':id?', selected?.id) +
          `?start_date=${runTimeConfig.MIN_TRADE_DATA_DATE}&end_date=${runTimeConfig.MAX_TRADE_DATA_DATE}`,
      );
    }
  };

  return (
    <Card>
      <StyledCardHeader
        title={
          <Typography color="white" variant="subtitle1">
            Quick View Panel
          </Typography>
        }
      />
      {isFetching ? (
        <SkeletonContent />
      ) : (
        <StyledCardContent>
          <Box>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} mb={1}>
              <LogoCompany domain={first(data.websites)} logoURL={data.logo_url || ''} width={40} height={40} />
              <ButtonBookmark
                iconProps={{ sx: { fontSize: 12 } }}
                buttonProps={{
                  size: 'medium',
                  onClick: () => handleOpenBookmarkNoteForm(selected?.id || '', selected?.has_bookmark || false),
                }}
                isMarked={isBookmark}
              />
            </Stack>
            <Typography color={theme.palette.primary.main} variant="subtitle1">
              {(data?.name || '').toUpperCase() || UnknownText}
            </Typography>
          </Box>

          <Stack spacing={1}>
            {data?.full_address && (
              <InformationItem
                theme={theme}
                icon={<MapPin />}
                label={(data?.full_address || UnknownText).toUpperCase()}
              />
            )}
            {!isEmpty(data?.phone_numbers) && (
              <InformationItem theme={theme} icon={<Phone />} label={getArrayDefault(data?.phone_numbers).join(', ')} />
            )}
            {!isEmpty(data?.emails) && (
              <InformationItem theme={theme} icon={<Mail />} label={getArrayDefault(data?.emails).join(', ')} />
            )}
            {!isEmpty(data?.websites) && (
              <InformationItem theme={theme} icon={<Globe />} label={getArrayDefault(data?.websites).join(', ')} />
            )}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography gap={1} display={'flex'} variant="subtitle2">
              Company Number:{' '}
              <Typography color={theme.palette.text.secondary} variant="body2">
                {(data?.company_code || '').toUpperCase() || UnknownText}
              </Typography>
            </Typography>

            <Typography gap={1} display={'flex'} variant="subtitle2">
              Incorporation Date:{' '}
              <Typography color={theme.palette.text.secondary} variant="body2">
                {data?.date_incorporated
                  ? formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data?.date_incorporated)
                  : UnknownText}
              </Typography>
            </Typography>

            <Box>
              <Typography display={'inline'} variant="subtitle2">
                Company Type:
              </Typography>
              <Typography ml={1} display={'inline'} color={theme.palette.text.secondary} variant="body2">
                {convertToStartCase(data?.company_type || '') || UnknownText}
              </Typography>
            </Box>

            <Typography gap={1} display={'flex'} variant="subtitle2">
              Jurisdiction:{' '}
              <Typography color={theme.palette.text.secondary} variant="body2">
                {convertToStartCase(data?.jurisdiction || '') || UnknownText}
              </Typography>
            </Typography>
          </Stack>

          {/* ----------- TODO: Re-open later ----------- */}
          {/* <Divider />
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            <Typography gap={1} display={'flex'} variant="subtitle2">
              Type Data
            </Typography>
            <Tooltip
              title={
                <p>
                  We have data from the Global Legal Entity Identifier Foundation (GLEIF) for this jurisdiction.{' '}
                  <Link
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                    to={LINK_GLEIF_SOURCE}
                    target="_blank"
                    color="white"
                  >
                    Learn more about GLEIF.
                  </Link>
                </p>
              }
            >
              <Chip
                color="secondary"
                label="GLEIF Data"
                size="small"
                clickable={false}
                sx={{
                  '&:hover': {
                    color: theme.palette.secondary.main,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  },
                }}
              />
            </Tooltip>
            {isCompleteData && (
              <Tooltip title="We have complete government registry information for companies in this jurisdiction.">
                <Chip
                  sx={{
                    '&:hover': {
                      color: theme.palette.success.main,
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                    },
                  }}
                  color="success"
                  label="Company Registry Data"
                  size="small"
                  clickable={false}
                />
              </Tooltip>
            )}
            {isDeadData && (
              <Tooltip title="The government in this jurisdiction does not publicly share company registration details with third parties. ">
                <Chip
                  sx={{
                    '&:hover': {
                      color: theme.palette.error.main,
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                  color="error"
                  label="Government Withheld"
                  size="small"
                  clickable={false}
                />
              </Tooltip>
            )}
            {isPendingData && (
              <Tooltip title="We are currently processing data for this jurisdiction. It will be available soon. Check our roadmap for updates.">
                <Chip
                  color="secondary"
                  label="Registration Data Pending"
                  size="small"
                  clickable={false}
                  sx={{
                    '&:hover': {
                      color: theme.palette.secondary.main,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                />
              </Tooltip>
            )}
          </Stack> */}
          <Divider />
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            <Typography gap={1} display={'flex'} variant="subtitle2">
              Profile Type
            </Typography>
            {companyProfileTags.map((CompanyProfileTag, index) => {
              return <CompanyProfileTag colorType={'transparent'} key={index} />;
            })}
          </Stack>

          {!isEmpty(data.hs_codes) && (
            <Stack gap={1} flexWrap={'wrap'} alignItems={'center'} direction={'row'}>
              <Typography variant="subtitle2">HS Code</Typography>
              {getArrayDefault(data.hs_codes).map((code: string) => (
                <Chip key={code} size="small" color="default" label={code} />
              ))}
            </Stack>
          )}

          {!isEmpty(data.products) && (
            <Stack gap={1} flexWrap={'wrap'} alignItems={'center'} direction={'row'}>
              <Typography variant="subtitle2">Products</Typography>
              {getArrayDefault(data.products).map((code: string) => (
                <Chip key={code} size="small" color="default" label={code} />
              ))}
            </Stack>
          )}

          <ButtonWithLoading
            size="large"
            variant="contained"
            isRounded
            disabled={isFetching}
            onClick={navigateToDetail}
            startIcon={
              selected?.check_entity_id_type_created_30_days_result ? (
                <CheckedCredit fontSize="small" />
              ) : pointsView.planName ? (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={0.5}
                  py={0.5}
                  px={1}
                  borderRadius={theme.shape.borderRadius}
                  bgcolor="rgba(255, 255, 255, 0.12)"
                >
                  <Typography variant="subtitle2" component="span" color="white">
                    {pointsView.creditCosts}
                  </Typography>{' '}
                  <CreditCoinIcon fontSize="small" />
                </Stack>
              ) : (
                <CreditCoinIcon fontSize="small" />
              )
            }
          >
            Profile Detail <ArrowForward sx={{ ml: 1 }} fontSize="small" />
          </ButtonWithLoading>
        </StyledCardContent>
      )}
    </Card>
  );
};

export default CompanyProfileDetail;

const InformationItem = ({ theme, icon, label }: { theme: Theme; icon: ReactNode; label: string }) => {
  return (
    <Stack gap={1} direction={'row'} alignItems="center">
      <IconFeather
        muiProps={{
          sx: {
            color: theme.palette.text.secondary,
            fontSize: 12,
          },
        }}
        icon={icon}
      />
      <Typography color={theme.palette.text.secondary} variant="caption">
        {label}
      </Typography>
    </Stack>
  );
};

const SkeletonContent = () => {
  return (
    <StyledCardContent>
      <Box>
        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} mb={1}>
          <Skeleton animation="wave" variant="rounded" width={24} />
          <Skeleton animation="wave" variant="rectangular" width={24} />
        </Stack>
        <Skeleton animation="wave" variant="rectangular" width={60} />
      </Box>
      <Stack spacing={1}>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={24} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={24} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
        <Skeleton animation="wave" variant="rectangular" width={120} />
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={24} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={24} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
      </Stack>
      <Divider />
      <Stack spacing={1}>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={120} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={120} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={120} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={120} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
        <Stack gap={1} direction={'row'} alignItems="center">
          <Skeleton animation="wave" variant="rounded" width={120} />
          <Skeleton animation="wave" variant="rectangular" width={120} />
        </Stack>
      </Stack>
      <Divider />
      <Stack gap={1} flexWrap={'wrap'} alignItems={'center'} direction={'row'}>
        <Skeleton animation="wave" variant="rectangular" width={60} />
        <Skeleton animation="wave" variant="rectangular" width={120} />
      </Stack>
      <Stack gap={1} flexWrap={'wrap'} alignItems={'center'} direction={'row'}>
        <Skeleton animation="wave" variant="rectangular" width={60} />
        <Skeleton animation="wave" variant="rectangular" width={120} />
      </Stack>
      <Divider />
      <Stack gap={1} flexWrap={'wrap'} alignItems={'center'} direction={'row'}>
        <Skeleton animation="wave" variant="rectangular" width={60} />
        <Skeleton animation="wave" variant="rectangular" width={120} />
      </Stack>
      <Stack gap={1} flexWrap={'wrap'} alignItems={'center'} direction={'row'}>
        <Skeleton animation="wave" variant="rectangular" width={60} />
        <Skeleton animation="wave" variant="rectangular" width={120} />
      </Stack>
      <Skeleton animation="wave" height={56} />
    </StyledCardContent>
  );
};
